import React, { Fragment } from 'react';
import { styled } from '@mui/material';
import { useHistory } from 'react-router-dom';

import { useViewport } from 'contexts/viewport';
import { useUser2 } from 'contexts/user2';
import {
    Box,
    Button,
    Card,
    Divider,
    Icon,
    IconButton,
    ProgressBar,
    Stack,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Tag,
    Text,
    Tooltip,
} from 'shell/components';
import { getUserPreferredFullName } from 'utils/helper';

const AllTeams = ({ groupList = [], suggestionsList, handleSetGroupId, viewSuggestions }) => {
    const history = useHistory();
    const { device } = useViewport();
    const { studentAccountUsers } = useUser2();

    const handleClickGroup = (groupId) => {
        handleSetGroupId(groupId);
        history.push(`/overview/${groupId}`);
    };

    const renderSuggestions = (groupId, suggestions) => {
        const moreThanTwo = Boolean(suggestions.length > 2);
        if (moreThanTwo) suggestions = suggestions.slice(0, 2);
        return (
            <Stack divider={<Divider />} spacing={3}>
                {suggestions.map(({ text }, index) => {
                    return (
                        <Fragment key={index}>
                            <Stack alignItems={'center'} direction={'row'} spacing={2}>
                                <CustomIcon kind={'error'} />
                                <Text color={'inherit'} variant={'p2'}>
                                    {text}
                                </Text>
                            </Stack>
                        </Fragment>
                    );
                })}
                {moreThanTwo ? (
                    <CustomButton
                        endIcon={<Icon kind={'chevronRight'} />}
                        variant={'tertiary'}
                        onClick={(e) => {
                            e.stopPropagation();
                            viewSuggestions(groupId);
                        }}
                    >
                        View all (3)
                    </CustomButton>
                ) : null}
            </Stack>
        );
    };

    return device === 'mobile' ? (
        <Stack spacing={4}>
            {groupList.map((group) => {
                const groupSuggestion = suggestionsList.find(({ group_id }) => group_id === group.id);
                const members = studentAccountUsers?.filter((student) => student.group_id === group.id);
                return (
                    <Card key={group.id} onClick={() => handleClickGroup(group.id)}>
                        <Stack spacing={2}>
                            <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                                <Text variant={'p1'} weight={'semibold'}>
                                    {members.length === 1 ? getUserPreferredFullName(members[0]) : group.name}
                                </Text>
                                <Stack direction={'row'} spacing={2}>
                                    {groupSuggestion && groupSuggestion.suggestions.length ? (
                                        <CustomIconButton
                                            kind={'error'}
                                            size={'xsmall'}
                                            variant={'circle'}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                viewSuggestions(group.id);
                                            }}
                                        />
                                    ) : null}
                                    <Tag type={'progress'} value={group.status} />
                                </Stack>
                            </Stack>
                            <ProgressBar
                                aria-label={`team progress is at ${Number(group.progress)} percent`}
                                value={Number(group.progress)}
                            />
                            <Stack direction={'row'} justifyContent={'space-between'}>
                                <Text variant={'p2'}>Progress</Text>
                                <Text variant={'p2'}>{group.progress || 0}%</Text>
                            </Stack>
                        </Stack>
                    </Card>
                );
            })}
        </Stack>
    ) : (
        <CustomBox>
            <CustomTable>
                <TableHead>
                    <TableRow>
                        <TableCell>Team Name</TableCell>
                        <TableCell>Progress</TableCell>
                        <TableCell>Percentage</TableCell>
                        <TableCell align={'center'}>Alerts</TableCell>
                        <TableCell>Status</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {groupList.map((group) => {
                        const groupSuggestion = suggestionsList.find(({ group_id }) => group_id === group.id);
                        const members = studentAccountUsers?.filter((student) => student.group_id === group.id);
                        return (
                            <TableRow key={group.id} onClick={() => handleClickGroup(group.id)}>
                                <TableCell>
                                    {members.length === 1 ? getUserPreferredFullName(members[0]) : group.name}
                                </TableCell>
                                <TableCell>
                                    <ProgressBar
                                        aria-label={`team progress is at ${Number(group.progress)} percent`}
                                        value={Number(group.progress)}
                                    />
                                </TableCell>
                                <TableCell>{`${group.progress}%`}</TableCell>
                                <TableCell align={'center'}>
                                    {groupSuggestion && groupSuggestion.suggestions.length ? (
                                        <Tooltip
                                            content={renderSuggestions(group.id, groupSuggestion.suggestions)}
                                            type={'alert'}
                                        />
                                    ) : null}
                                </TableCell>
                                <TableCell>
                                    <Tag sx={{ maxWidth: '4.25rem' }} type={'progress'} value={group.status} />
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </CustomTable>
        </CustomBox>
    );
};

const CustomIconButton = styled(IconButton)(({ theme }) => {
    return {
        background: theme.palette.ensightful.error[700],
        '&:hover': {
            background: theme.palette.ensightful.error[700],
        },
        '&:active': {
            background: theme.palette.ensightful.error[800],
        },
    };
});

const CustomBox = styled(Box)(({ theme }) => {
    return {
        borderRadius: '12px 12px 0 0',
        minHeight: 'calc(100vh - 198px)',
        padding: theme.spacing(1, 4, 0),
        background: theme.palette.ensightful.shades[0],
    };
});

const CustomTable = styled(Table)(() => {
    return {
        '& tr > th:nth-of-type(1)': {
            width: '20%',
        },
        '& tr > th:nth-of-type(2)': {
            width: '30%',
        },
        '& tr > th:nth-of-type(3)': {
            width: '10%',
        },
        '& tr > th:nth-of-type(4)': {
            width: '10%',
        },
        '& tr > th:nth-of-type(5)': {
            width: '1%',
        },
        '& tr > td:nth-of-type(1)': {
            width: '20%',
        },
        '& tr > td:nth-of-type(2)': {
            width: '30%',
        },
        '& tr > td:nth-of-type(3)': {
            width: '10%',
        },
        '& tr > td:nth-of-type(4)': {
            width: '10%',
        },
        '& tr > td:nth-of-type(5)': {
            width: '1%',
        },
    };
});

const CustomIcon = styled(Icon)(({ theme }) => {
    return {
        fontSize: '1.5rem',
        padding: '0.3125rem',
        color: theme.palette.ensightful.neutral[200],
    };
});

const CustomButton = styled(Button)(({ theme }) => {
    return {
        color: 'inherit',
        alignSelf: 'flex-end',

        '&:hover': {
            color: theme.palette.ensightful.neutral[200],
            border: 'none',
        },
    };
});

export default AllTeams;
